import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import _ from 'lodash';
import { ConfirmationService, MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import {
  DataMasterAdminGroup,
  DataMasterViewerGroup,
  LapDipRequestRoles,
  LapDipRoles,
  ProductDevelopmentAdminGroup,
  ProductDevelopmentEditorGroup,
  ProductDevelopmentViewerGroup,
  TestLabDipAdminGroup,
} from './models/authorization/enum/user-group.enum';
import { UserAuthenticationService } from './services/user-authentication.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { MasterDataActionUtil } from './utilities/master-data-action-util';
import { MenuItem, ReportBugService } from '@card-software/library';
import { MasterDataService, RolesBootstrapService, Site, SiteBootstrapService, SiteService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'item-ui';
  isLoginPage = false;
  readonly environment = environment.environment;

  menuItems: MenuItem[] = [
    {
      displayName: 'Product Development',
      routerLink: '',
      redirect: false,
      children: [
        {
          displayName: 'Yarns',
          routerLink: '/yarns',
          redirect: true,
          roles: [ProductDevelopmentViewerGroup.YarnViewer, ProductDevelopmentAdminGroup.YarnAdmin],
        },
        {
          displayName: 'Greiges',
          routerLink: '/greiges',
          redirect: true,
          roles: [
            ProductDevelopmentViewerGroup.GreigeViewer,
            ProductDevelopmentEditorGroup.GreigeEditor,
            ProductDevelopmentAdminGroup.GreigeAdmin,
          ],
        },
        {
          displayName: 'Fabrics',
          routerLink: '/fabric',
          redirect: true,
          roles: [
            ProductDevelopmentViewerGroup.FabricViewer,
            ProductDevelopmentEditorGroup.FabricEditor,
            ProductDevelopmentAdminGroup.FabricAdmin,
          ],
        },
        {
          displayName: 'Fabric Categories',
          routerLink: '/fabric-categories',
          redirect: true,
          roles: [ProductDevelopmentViewerGroup.FabricCategoriesViewer, ProductDevelopmentAdminGroup.FabricCategoriesAdmin],
        },
        {
          displayName: 'Fiber Categories',
          routerLink: '/fiber-categories',
          redirect: true,
          roles: [ProductDevelopmentViewerGroup.FiberCategoriesViewer, ProductDevelopmentAdminGroup.FiberCategoriesAdmin],
        },
      ],
    },
    {
      displayName: 'Item Master',
      routerLink: '',
      redirect: false,
      children: [
        {
          displayName: 'Item Master',
          routerLink: '/item-master',
          redirect: true,
          roles: [DataMasterViewerGroup.ItemMasterViewer, DataMasterAdminGroup.ItemMasterAdmin],
        },
        {
          displayName: 'Item Categories',
          routerLink: '/item-categories',
          redirect: true,
          roles: [DataMasterViewerGroup.ItemCategoriesViewer, DataMasterAdminGroup.ItemCategoriesAdmin],
        },
        {
          displayName: 'Code Creation Requests',
          routerLink: '/code-creation-requests',
          redirect: true,
          roles: [DataMasterViewerGroup.ItemCodeRequestViewer, DataMasterAdminGroup.ItemMasterAdmin],
        },
      ],
    },
    {
      displayName: 'Dyeing',
      routerLink: '',
      redirect: false,
      children: [
        {
          displayName: 'Requests',
          routerLink: '/lab-dip-requests',
          redirect: true,
          roles: [LapDipRequestRoles.LabDipRequestViewer, LapDipRequestRoles.LabDipRequestEditor, LapDipRequestRoles.LabDipRequestAdmin],
        },
        {
          displayName: 'Lab Dips',
          routerLink: '/lab-dips',
          redirect: true,
          roles: [LapDipRoles.LabDipViewer, LapDipRoles.LabDipEditor, LapDipRoles.LabDipAdmin],
        },
      ],
    },
    {
      displayName: 'Recipe',
      routerLink: '',
      redirect: false,
      children: [
        {
          displayName: 'Product Recipes',
          routerLink: '/product-recipes',
          redirect: true,
          roles: [
            ProductDevelopmentViewerGroup.ProductRecipeViewer,
            ProductDevelopmentEditorGroup.ProductRecipeEditor,
            ProductDevelopmentAdminGroup.ProductRecipeAdmin,
          ],
        },
      ],
    },
    {
      displayName: 'Engineering',
      routerLink: '',
      redirect: false,
      children: [
        {
          displayName: 'Processes',
          routerLink: '/processes',
          redirect: true,
          roles: [
            TestLabDipAdminGroup.TestLabDipAdmin,
            ProductDevelopmentAdminGroup.EngineeringAdmin,
            ProductDevelopmentEditorGroup.EngineeringEditor,
            ProductDevelopmentViewerGroup.EngineeringViewer,
          ],
        },
        {
          displayName: 'Steps',
          routerLink: '/steps',
          redirect: true,
          roles: [
            TestLabDipAdminGroup.TestLabDipAdmin,
            ProductDevelopmentAdminGroup.EngineeringAdmin,
            ProductDevelopmentEditorGroup.EngineeringEditor,
            ProductDevelopmentViewerGroup.EngineeringViewer,
          ],
        },
        {
          displayName: 'Parameters',
          routerLink: '/parameters',
          redirect: true,
          roles: [
            TestLabDipAdminGroup.TestLabDipAdmin,
            ProductDevelopmentAdminGroup.EngineeringAdmin,
            ProductDevelopmentEditorGroup.EngineeringEditor,
            ProductDevelopmentViewerGroup.EngineeringViewer,
          ],
        },
      ],
    },
  ];

  constructor(
    private router: Router,
    private userAuthenticationService: UserAuthenticationService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private siteBootstrapService: SiteBootstrapService,
    private rolesBootstrapService: RolesBootstrapService,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isLoginPage = _.includes(event.url, '/login');
      }
    });
    GlobalMessageUtil.init(this.messageService);
    MasterDataActionUtil.init(this.confirmationService);

    const token = this.userAuthenticationService.getStoredJwtToken();
    if (token) {
      MasterDataService.initialize(environment.proxyUrls.itemApi, token);
      ReportBugService.initialize(environment.proxyUrls.itemApi, token);
      this.rolesBootstrapService.initializeRoles(token);
      this.siteBootstrapService.initializeSites(token);
    }
  }

  get isLoggedIn() {
    return this.userAuthenticationService.isUserAuthenticated;
  }

  get userLite() {
    return this.userAuthenticationService.userLite;
  }

  get userToken() {
    return this.userAuthenticationService.getStoredJwtToken() || '';
  }

  get showSidebar(): boolean {
    const isLoginPage = this.router.url.includes('/login');
    return this.userAuthenticationService.isUserAuthenticated && !isLoginPage;
  }

  get sites(): Site[] {
    return SiteService.getSites();
  }

  get selectedSiteId(): string {
    return SiteService.getSelectedSiteId() || '';
  }

  onSiteChanged(siteId: string) {
    SiteService.setSelectedSite(siteId);
  }

  onLogout() {
    this.userAuthenticationService.logout();
  }
}
